import { Link, useLocation } from 'react-router-dom';
import type { BookedSession } from '../api/use-cart.js';
import { usePeople } from '../api/use-people.js';

/**
 * Booked.
 *
 * The heading says "Booked" rather than counting them — the list underneath is
 * the count, and a number in a heading is one more thing that can disagree with
 * what is on the screen.
 *
 * It reads what checkout returned rather than fetching again: the answer is
 * already in hand, and a second request would be a second chance to show
 * something different from what was just confirmed. Arriving here directly —
 * a refresh, or a pasted URL — has nothing to show, and says so plainly rather
 * than pretending a booking happened.
 */
export function BookedRoute() {
  const location = useLocation();
  const booked = (location.state as { booked?: BookedSession[] } | null)?.booked ?? [];

  // WHO, NOT HOW MANY. The row said "1 athlete" — a count, on the one screen
  // where a parent is checking that the right child got the right hour. The
  // booking carries the ids and the account already knows these children by
  // name, so it says the names. A count is the fallback for the case where a
  // name cannot be found, not the thing to print by choice.
  const people = usePeople();
  const names = new Map<string, string>();
  if (people.state.status === 'ready') {
    for (const person of people.state.people) {
      names.set(person.client_id, `${person.first_name} ${person.last_name}`.trim());
    }
  }

  function who(ids: string[]): string {
    const found = ids.map((id) => names.get(id)).filter((name): name is string => Boolean(name));
    if (found.length !== ids.length) {
      return ids.length === 1 ? '1 athlete' : `${ids.length} athletes`;
    }
    return found.join(' and ');
  }

  if (booked.length === 0) {
    return (
      <>
        <h2>Nothing to show here</h2>
        <p className="empty">
          This page shows what you have just booked. Your lessons are in the lessons list.
        </p>
        <Link className="primary" to="/lessons">
          See my lessons
        </Link>
      </>
    );
  }

  return (
    <>
      <h2>Booked</h2>

      {booked.map((session) => (
        <div key={session.booking_id} className="fact">
          <span className="label">{when(session.slot_start)}</span>
          <span className="value">{who(session.attendee_client_ids)}</span>
        </div>
      ))}

      <Link className="primary" to="/lessons">
        See my lessons
      </Link>
    </>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
